import MedenticAuthEntryPage from "@shared/ui/MedenticAuthEntryPage";
import React, { useState, useMemo } from "react";
import useApiRequest from "@shared/hooks/common/useApiRequest";
import { PageTypes, HealthcareCompanyType } from "@shared/types/commonTypes";
import { useCountries } from "@shared/hooks/common/useCountries";
import { useMutation, useQueryClient } from "@tanstack/react-query";
import { useTranslation } from "react-i18next";
import { useHealthcareCompanyMeta, CompanyType } from "@shared/hooks/common/useHealthcareCompanyMeta";
import {
  FormLayout,
  TextField,
  Select,
  InlineError,
  Button,
  Box,
  InlineStack,
} from "@medentic/polaris-react";
import { setLocalStorage } from "@shared/lib";
import { useNavigate } from "react-router-dom";
import { ROOTS } from "@app/config/constants";
import usePageType from "@shared/hooks/navigation/usePageType";

const AddHealthcareCompany: React.FC = () => {
  const { t } = useTranslation();
  const queryClient = useQueryClient();
  const { POST } = useApiRequest();

  const navigate = useNavigate();
  const pageType = usePageType();
  const [selectedType, setSelectedType] = useState<string | null>(null);
  const [country, setCountry] = useState<string>("BGR");
  const [business, setBusiness] = useState<string>("");
  const [taxId, setTaxId] = useState<string>("");

  const [typeError, setTypeError] = useState<string | null>(null);
  const [countryError, setCountryError] = useState<string | null>(null);
  const [businessError, setBusinessError] = useState<string | null>(null);
  const [taxIdError, setTaxIdError] = useState<string | null>(null);

  const {
    companyTypes,
    isLoading: isTypesLoading,
    error: metaError,
  } = useHealthcareCompanyMeta();

  // Determine if we're adding a clinic or event organizer based on the URL
  const isEventMode = pageType === PageTypes.EVENT;
  
  // Filter company types based on the mode
  const filteredCompanyTypes = useMemo(() => {
    if (isEventMode) {
      // For event mode, show only event_organizer types
      return companyTypes.filter(
        (ct) => ct.id === HealthcareCompanyType.EVENT_ORGANIZER
      );
    } else {
      // For clinic mode, show all except event_organizer
      return companyTypes.filter(
        (ct) => ct.id !== HealthcareCompanyType.EVENT_ORGANIZER
      );
    }
  }, [companyTypes, isEventMode]);

  const type = useMemo(() => {
    if (
      selectedType &&
      filteredCompanyTypes.some((companyType) => companyType.id === selectedType)
    ) {
      return selectedType;
    }
    return filteredCompanyTypes[0]?.id ?? "";
  }, [selectedType, filteredCompanyTypes]);

  const { countries: countryData, isLoading: isCountriesLoading } = useCountries();

  const createClinic = useMutation({
    mutationFn: (newCompany: Record<string, string>) =>
      POST("/api/healthcare-companies", newCompany),
    // eslint-disable-next-line @typescript-eslint/no-explicit-any
    onSuccess: (response: any) => {
      queryClient.invalidateQueries({ queryKey: ["healthcare-companies"] });
      
      // Navigate and store based on the mode
      if (isEventMode) {
        setLocalStorage("__LAST_VISITED_E_ID", response?.id);
        navigate(`${ROOTS.EVENTS}/${response?.id}`);
      } else {
        setLocalStorage("__LAST_VISITED_C_ID", response?.id);
        navigate(`${ROOTS.CLINIC}/${response?.id}`);
      }
    },
    onError: () => {
      setTypeError(t("addClinic.errorCreatingCompany"));
    },
  });

  const handleCreate = () => {
    let valid = true;

    if (!type) {
      setTypeError(t("addClinic.typeError"));
      valid = false;
    } else {
      setTypeError(null);
    }

    if (!country) {
      setCountryError(t("addClinic.countryError"));
      valid = false;
    } else {
      setCountryError(null);
    }

    if (!business) {
      setBusinessError(t("addClinic.businessNameError"));
      valid = false;
    } else {
      setBusinessError(null);
    }

    if (!taxId) {
      setTaxIdError(t("addClinic.taxIdError"));
      valid = false;
    } else {
      setTaxIdError(null);
    }

    if (valid) {
      createClinic.mutate({
        type,
        country,
        business,
        taxId,
      });
    }
  };

  return (
    <MedenticAuthEntryPage>
      <FormLayout>
        <Select
          label={t("addClinic.typeLabel")}
          options={filteredCompanyTypes.map((companyType: CompanyType) => ({
            label: companyType.label,
            value: companyType.id,
          }))}
          value={type}
          onChange={(value) => setSelectedType(value ?? "")}
          disabled={isTypesLoading || filteredCompanyTypes.length === 0}
          requiredIndicator
          helpText={
            metaError 
              ? t("addClinic.typesLoadError")
              : isTypesLoading 
                ? t("addClinic.typesLoading")
                : filteredCompanyTypes.length === 0 
                  ? t("addClinic.typesEmpty")
                  : undefined
          }
        />
        {typeError && <InlineError message={typeError} fieldID="type" />}
        {metaError && (
          <InlineError
            message={t("addClinic.typesLoadFailed")}
            fieldID="meta-error"
          />
        )}

        <Select
          label={t("addClinic.countryLabel")}
          options={countryData.map((country) => ({
            label: country.name,
            value: country.iso3,
          }))}
          value={country}
          onChange={(value) => setCountry(value ?? "")}
          disabled={isCountriesLoading}
          requiredIndicator
        />
        {countryError && (
          <InlineError message={countryError} fieldID="country" />
        )}

        <TextField
          label={t("addClinic.businessNameLabel")}
          value={business}
          onChange={(value) => setBusiness(value)}
          autoComplete="off"
          requiredIndicator
        />
        {businessError && (
          <InlineError message={businessError} fieldID="business" />
        )}

        <TextField
          label={t("addClinic.taxIdLabel")}
          value={taxId}
          onChange={(value) => setTaxId(value)}
          autoComplete="off"
          requiredIndicator
        />
        {taxIdError && <InlineError message={taxIdError} fieldID="taxId" />}
      </FormLayout>
      <Box paddingBlockStart="500">
        <InlineStack gap="400" align="end">
          <Button onClick={() => navigate(-1)}>
            {t("addClinic.cancelButton")}
          </Button>
          <Button
            variant="primary"
            onClick={handleCreate}
            loading={createClinic.isPending}
          >
            {t("addClinic.createButton")}
          </Button>
        </InlineStack>
      </Box>
    </MedenticAuthEntryPage>
  );
};

export default AddHealthcareCompany;
